import { BRAND, PROG_GRAD, ACCENTS } from "../theme";
import Icon from "./Icon";

const STATUS = {
  Active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20",
  Completed: "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20",
  "On Hold": "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
  Discontinued: "bg-red-50 text-red-700 ring-1 ring-red-600/20",
  Expired: "bg-red-50 text-red-700 ring-1 ring-red-600/20",
  Requested: "bg-slate-50 text-slate-600 ring-1 ring-slate-600/20",
  Approved: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20",
  Reassigned: "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20",
  Recorded: "bg-slate-50 text-slate-600 ring-1 ring-slate-600/20",
  Hybrid: "bg-violet-50 text-violet-700 ring-1 ring-violet-600/20",
  Live: "bg-sky-50 text-sky-700 ring-1 ring-sky-600/20",
};

export function Btn({ variant = "primary", className = "", style = {}, ...p }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-semibold px-4 py-2.5 transition-all duration-200 active:scale-[.97] disabled:opacity-40 disabled:pointer-events-none select-none cursor-pointer";
  const map = {
    primary: "text-white shadow-sm hover:shadow-md hover:brightness-110",
    dark: "bg-slate-900 text-white hover:bg-slate-800 shadow-sm",
    outline: "border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 bg-white shadow-sm",
    danger: "border border-red-200 text-red-600 hover:bg-red-50 bg-white shadow-sm",
    ghost: "text-slate-500 hover:text-slate-800 hover:bg-slate-100",
  };
  const brand = variant === "primary" ? { backgroundColor: BRAND, ...style } : style;
  return <button className={`${base} ${map[variant]} ${className}`} style={brand} {...p} />;
}

export const Card = ({ className = "", ...p }) =>
  <div className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm shadow-slate-200/50 ${className}`} {...p} />;

export const Badge = ({ children }) =>
  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide ${STATUS[children] || "bg-slate-50 text-slate-600 ring-1 ring-slate-600/20"}`}>{children}</span>;

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
    {children}
  </label>
);

export const Input = (p) =>
  <input {...p} className={`w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all ${p.className || ""}`} />;

export const Select = ({ children, ...p }) =>
  <select {...p} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all">{children}</select>;

export const ProgressBar = ({ value, tall }) => (
  <div className={`${tall ? "h-3" : "h-2"} w-full rounded-full bg-slate-100 overflow-hidden`}>
    <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${value}%`, backgroundImage: PROG_GRAD }} />
  </div>
);

export const PageHead = ({ title, sub, right }) => (
  <div className="flex items-start justify-between mb-8 gap-4">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h1>
      {sub && <p className="text-slate-500 text-sm mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

const AVATAR_COLORS = [
  { bg: "#4F46E5", fg: "#FFFFFF" },  // indigo-600
  { bg: "#6366F1", fg: "#FFFFFF" },  // indigo-500
  { bg: "#7C3AED", fg: "#FFFFFF" },  // violet-600
  { bg: "#0EA5E9", fg: "#FFFFFF" },  // sky-500
  { bg: "#4338CA", fg: "#FFFFFF" },  // indigo-700
  { bg: "#8B5CF6", fg: "#FFFFFF" },  // violet-500
  { bg: "#0284C7", fg: "#FFFFFF" },  // sky-600
  { bg: "#312E81", fg: "#FFFFFF" },  // indigo-900
];
export const Avatar = ({ name, size = 36 }) => {
  const n = name || "?";
  const initials = n.split(" ").filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join("");
  const hash = [...n].reduce((a, c) => a + c.charCodeAt(0), 0);
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  const fs = size < 30 ? 10 : size < 40 ? 12 : 14;
  return (
    <div className="rounded-full shrink-0 ring-2 ring-white shadow-sm flex items-center justify-center font-semibold select-none"
      style={{ width: size, height: size, backgroundColor: color.bg, color: color.fg, fontSize: fs, letterSpacing: "0.02em" }}
      title={name}>
      {initials}
    </div>
  );
};

export const StatTile = ({ label, value, icon, i = 0 }) => {
  const accent = ACCENTS[i % ACCENTS.length];
  return (
    <Card className="p-5 flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center ring-1" style={{ backgroundColor: accent.bg, color: accent.fg, ringColor: accent.ring }}>
        <Icon name={icon} size={20} />
      </div>
      <div>
        <div className="text-[13px] font-medium text-slate-500">{label}</div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{value}</div>
      </div>
    </Card>
  );
};

export const Toast = ({ msg }) => msg ? (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-xl bg-slate-900 text-white text-sm px-5 py-3 shadow-xl shadow-slate-900/20 backdrop-blur-sm fade-in">{msg}</div>
) : null;
